import { Button } from '@edsn/pie-components';
import * as Portal from '@radix-ui/react-portal';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Card } from '#pie/components/card/Card';
import { Stack } from '#pie/components/stack/Stack';
import { Text } from '#pie/components/text/Text';
import { useToast } from '#pie/components/toast/ToastContext';
import {
  getGetNotificationQueryKey,
  type NotificationDto,
  type UpdateNotificationDto,
  useGetNotification,
  usePutNotification,
} from '#pie/internal/api/pie-bff';
import { typedFormFields } from '#pie/utils/typedFormFields';
import { NotificationsCategories } from './NotificationsCategories';

export const Notifications = () => {
  const { t: tom } = useTranslation('organisationManagement');
  const { FormText, FormToggle } = typedFormFields<NotificationDto>();
  const formMethods = useForm<NotificationDto>();
  const { data } = useGetNotification();
  const { handleSubmit } = formMethods;
  const ref = useRef<HTMLFormElement>(null);
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const isChecked = formMethods.watch('enabled');

  useEffect(() => {
    formMethods.reset({ ...data });
  }, [data]);

  const { mutate, isPending: isLoading } = usePutNotification({
    mutation: {
      onError: (error) => {
        addToast({
          message: `${tom('toast.error.message_adjusting_email')}`,
          title: `${tom('toast.error.title_adjusting_email')}`,
          type: 'error',
          error: error,
        });
      },
      onSuccess: (_data, variables) => {
        addToast({
          message: `${tom('toast.success.message_adujsting_email', {
            email: variables.data.email,
          })}`,
          title: `${tom('toast.success.title_email_adjusted')}`,
          type: 'success',
        });
        queryClient.invalidateQueries({
          queryKey: getGetNotificationQueryKey(),
        });
      },
    },
  });

  const onSubmit: SubmitHandler<UpdateNotificationDto> = (data) => {
    mutate({ data });
  };

  return (
    <Stack gap="lg" className="mx-auto my-6 max-w-4xl">
      <Card
        size="md"
        heading={
          <Text as="h2" variant="h6" className="text-content-heading">
            {tom('settings.title_notifications')}
          </Text>
        }
      >
        <Stack gap="md">
          <Text className="">
            {tom('settings.description.email_notifications')}
          </Text>
          <FormProvider {...formMethods}>
            <form
              ref={ref}
              onSubmit={handleSubmit(onSubmit)}
              aria-label={tom('settings.description.email_notifications')}
            >
              <Stack gap="md">
                <FormToggle
                  label={tom('tab.notifications')}
                  option={{
                    label: tom('settings.toggle.label.email_notification'),
                  }}
                  name="enabled"
                />
                {isChecked && (
                  <FormText
                    fullWidth
                    name="email"
                    label={tom('email_label')}
                    rules={{ required: true }}
                  ></FormText>
                )}
              </Stack>
              <Portal.Root
                container={document.getElementById('pageheader-button')}
              >
                <Button
                  disabled={!formMethods.formState.isDirty}
                  action="secondary"
                  emphasis="bold"
                  onClick={() => {
                    ref.current?.requestSubmit();
                  }}
                  isLoading={isLoading}
                >
                  {tom('header.button.submit_changes')}
                </Button>
              </Portal.Root>
            </form>
          </FormProvider>
        </Stack>
      </Card>
      {isChecked && <NotificationsCategories />}
    </Stack>
  );
};
